import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import {
  ArchiveAdhocQuestionRequest,
  GetAdhocQuestionsRequest,
} from "@/types/requests";
import { AdhocQuestion, Run } from "@/types/resources";
import {
  GetAdhocQuestionsResponse,
  GetRunsResponse,
  UpdateAdhocQuestionResponse,
} from "@/types/responses";

export const useAdhocByUser = (
  userId: string,
  search: string = "",
  itemsPerPage: number = 10,
  createdAfter?: string,
  createdBefore?: string
) => {
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [questions, setQuestions] = useState<AdhocQuestion[]>([]);

  const client = useAxiosClient("/adhoc/questions");

  const getQuestions = useCallback(
    (p: number) => {
      const params: GetAdhocQuestionsRequest = {
        created_after: createdAfter,
        created_before: createdBefore,
        created_by_id: userId,
        items_per_page: itemsPerPage,
        page: p,
        text: search || undefined,
      };

      client
        .get<GetAdhocQuestionsResponse>("", { params })
        .then(({ data }) => {
          setCount(data.count);
          setQuestions((prev) =>
            p === 1 ? data.questions : [...prev, ...data.questions]
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "Unknown error");
        })
        .finally(() => {
          if (p === 1) setLoading(false);
          else setLoadingMore(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, itemsPerPage, search, createdAfter, createdBefore]
  );

  // Reset the list during render (not in an effect) when the query inputs
  // change, so the UI shows a fresh loading state while refetching.
  const inputsKey = JSON.stringify([
    userId,
    itemsPerPage,
    search,
    createdAfter,
    createdBefore,
  ]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setQuestions([]);
    setPage(1);
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    getQuestions(1);
  }, [getQuestions]);

  const loadMore = useCallback(() => {
    const next = page + 1;
    setLoadingMore(true);
    setPage(next);
    getQuestions(next);
  }, [getQuestions, page]);

  return {
    count,
    error,
    hasMore: questions.length < count,
    loading,
    loadingMore,
    loadMore,
    questions,
    setCount,
    setQuestions,
  };
};

export const useQuestionRuns = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<Run[]>([]);

  const client = useAxiosClient("/adhoc/questions");

  const fetch = useCallback(() => {
    client
      .get<GetRunsResponse>(`/${questionId}/runs`)
      .then(({ data }) => {
        setRuns(data.runs);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Unknown error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, questionId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  return { error, loading, refresh, runs };
};

export const useArchiveQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/adhoc/questions");

  const archiveQuestion = useCallback(
    async (questionId: string, archived: boolean) => {
      setError(null);
      setLoading(true);
      try {
        const data: ArchiveAdhocQuestionRequest = { archived };
        const response = await client.patch<UpdateAdhocQuestionResponse>(
          `/${questionId}`,
          data
        );
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { archiveQuestion, error, loading };
};

export const useDeleteQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/adhoc/questions");

  const deleteQuestion = useCallback(
    async (questionId: string) => {
      setError(null);
      setLoading(true);
      try {
        await client.delete(`/${questionId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { deleteQuestion, error, loading };
};
